import { Component, input, output, signal, viewChild } from '@angular/core';
import { GroupId, GroupVisibility } from '../model';
import { GroupDto } from '../../api/dtos';
import { IconComponent } from '../../shared/components/icon.component';
import { RouterLink } from '@angular/router';
import { DeleteGroupDialogComponent } from '../components/delete-group-dialog';
import { LazyLoadEvent, Table2Component } from '../../shared/components/table.component';
import { SynIconButtonComponent } from '@synergy-design-system/angular';

export interface PageChangeEvent {
  offset: number;
}

export interface DeleteGroupEvent {
  groupId: GroupId;
}

const VISIBILITY_TOOLTIP: Record<GroupVisibility, string> = {
  company: $localize`:@@groupList.visibilityTooltip.company:Anyone in your company can see this group.`,
  private: $localize`:@@groupList.visibilityTooltip.private:Only users with appropriate rights can see this group.`,
};

@Component({
  selector: 'app-group-list',
  templateUrl: './group-list.component.html',
  styles: [
    `
      button.p-button-text.p-button-secondary {
        border-radius: 0;
        padding: 4px;
        aspect-ratio: 1;
        width: auto;
      }

      button.p-button-text.p-button-secondary:enabled:hover {
        background-color: rgba(0, 0, 0, 0);
        color: var(--syn-color-primary-400);
      }

      .visibility-icon {
        fill: var(--gray-500);
      }
    `,
  ],
  imports: [DeleteGroupDialogComponent, Table2Component, RouterLink, IconComponent, SynIconButtonComponent],
})
export class GroupListComponent {
  groups = input<GroupDto[]>([]);
  rowCount = input(0);
  totalCount = input(0);
  loading = input(false);
  showDeleteButton = input(false);
  baseLink = input('managed-groups');

  onPageChange = output<PageChangeEvent>();
  onDelete = output<DeleteGroupEvent>();

  deleteDialog = viewChild<DeleteGroupDialogComponent>('dialog');

  groupToDelete = signal<GroupDto | undefined>(undefined);

  onLazyLoad(event: LazyLoadEvent) {
    this.onPageChange.emit({ offset: event.offset });
  }

  onDeleteButtonClicked(group: GroupDto) {
    this.groupToDelete.set(group);
    this.deleteDialog()?.show();
  }

  closeDeleteModal() {
    this.deleteDialog()?.hide();
    this.groupToDelete.set(undefined);
  }

  onDeleteGroupConfirmed() {
    const groupToDelete = this.groupToDelete();
    if (groupToDelete) {
      this.onDelete.emit({ groupId: groupToDelete.id });
    } else {
      console.error('onDeleteGroupConfirmed called without a groupToDelete');
    }

    this.closeDeleteModal();
  }

  getVisiblityIcon(visibility: GroupVisibility) {
    switch (visibility) {
      case 'company':
        return 'visibility';
      case 'private':
        return 'visibility_off';
    }
  }

  getVisibilityTooltip(visibility: GroupVisibility) {
    return VISIBILITY_TOOLTIP[visibility];
  }

  preventEventPropagation(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }
}
